import React, { useEffect, useState } from "react";
import { BarChart3, TrendingUp, Zap } from "lucide-react";
import api from "../../lib/api";

function fmtInr(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (isNaN(num)) return "—";
  if (num >= 1e7) return `₹${(num / 1e7).toFixed(1)}Cr`;
  if (num >= 1e5) return `₹${(num / 1e5).toFixed(1)}L`;
  return `₹${num.toLocaleString("en-IN")}`;
}

const METRIC_META = {
  trust_score:          { label: "Trust Score",   fmt: (v) => (v == null ? "—" : Math.round(v)), unit: "" },
  profile_completeness: { label: "Completeness",  fmt: (v) => (v == null ? "—" : `${Math.round(v)}%`), unit: "%" },
  arr:                  { label: "ARR",           fmt: fmtInr, unit: "" },
  annual_revenue:       { label: "Annual Rev.",   fmt: fmtInr, unit: "" },
  amount_required:      { label: "Funding Ask",   fmt: fmtInr, unit: "" },
};

function MiniBar({ your, p25, median, p75 }) {
  if (p75 == null || p25 == null) {
    return <div className="h-1.5 bg-navy/10 rounded-full" />;
  }
  const span = Math.max(1, Math.max(p75, your || 0) * 1.15);
  const pct = (v) => Math.min(100, Math.max(0, (v / span) * 100));
  return (
    <div className="relative h-3 mt-1">
      {/* IQR bar (p25 → p75) */}
      <div
        className="absolute top-1/2 -translate-y-1/2 h-1.5 bg-emerald-200/60 rounded-full"
        style={{ left: `${pct(p25)}%`, width: `${pct(p75) - pct(p25)}%` }}
      />
      {/* Median tick */}
      {median != null && (
        <div
          className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-emerald-600 rounded"
          style={{ left: `${pct(median)}%` }}
          title={`Peer median`}
        />
      )}
      {/* Your marker */}
      {your != null && (
        <div
          className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-gold shadow ring-2 ring-white"
          style={{ left: `calc(${pct(your)}% - 5px)` }}
          title={`You`}
        />
      )}
    </div>
  );
}

export default function PeerBenchmarkCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.get("/startup/peer-benchmark");
        if (!cancel) setData(res.data);
      } catch (e) { /* silent */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl border border-navy/10 bg-white p-5 animate-pulse" data-testid="peer-benchmark-loading">
        <div className="h-3 bg-navy/5 w-1/3 rounded mb-3" />
        <div className="h-16 bg-navy/5 w-full rounded" />
      </div>
    );
  }
  if (!data) return null;

  const scopeLabel = {
    "sector+stage": `${data.sector || "?"} · ${data.business_stage || "?"}`,
    "sector": `${data.sector || "?"} (all stages)`,
    "all_startups": "All approved startups",
  }[data.match_scope] || "Peers";

  return (
    <div className="rounded-xl border border-navy/10 bg-white shadow-sm" data-testid="peer-benchmark-card">
      <div className="px-5 py-3 border-b border-navy/10 bg-navy/[0.02] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 size={12} className="text-emerald-700" />
          <div className="overline text-emerald-700">Peer Benchmark</div>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted" data-testid="peer-scope">
          {scopeLabel} · {data.peer_count} peers
        </div>
      </div>

      <div className="p-5 space-y-3.5">
        {Object.entries(data.metrics).map(([k, m]) => {
          const meta = METRIC_META[k];
          if (!meta) return null;
          const smallN = (m.sample_size || 0) < 5;
          const pct = smallN ? null : m.percentile;
          const tone = pct == null ? "muted"
            : pct >= 75 ? "emerald"
            : pct >= 50 ? "amber"
            : "rose";
          const toneClass = {
            emerald: "text-emerald-700",
            amber:   "text-amber-700",
            rose:    "text-rose-600",
            muted:   "text-ink-muted",
          }[tone];
          return (
            <div key={k} data-testid={`peer-metric-${k}`}>
              <div className="flex items-center justify-between">
                <div className="text-xs text-ink">
                  <span className="font-heading">{meta.label}</span>
                  <span className="text-ink-muted ml-2 font-mono">{meta.fmt(m.your_value)}</span>
                </div>
                <div className={`text-[10px] font-mono uppercase tracking-widest ${toneClass}`}>
                  {smallN ? `n=${m.sample_size || 0}` : (pct == null ? "—" : `p${pct}`)}
                </div>
              </div>
              {!smallN && <MiniBar your={m.your_value} p25={m.p25} median={m.median} p75={m.p75} />}
              {!smallN && m.p75 != null && (
                <div className="flex items-center justify-between text-[9px] font-mono text-ink-muted mt-0.5">
                  <span>p25 {meta.fmt(m.p25)}</span>
                  <span>med {meta.fmt(m.median)}</span>
                  <span>p75 {meta.fmt(m.p75)}</span>
                </div>
              )}
              {smallN && (
                <div className="text-[10px] text-ink-muted mt-0.5 italic">Too few peers to benchmark yet.</div>
              )}
            </div>
          );
        })}
      </div>

      {data.biggest_gap && (
        <div className="border-t border-navy/10 px-5 py-3 bg-gradient-to-r from-emerald-50/50 to-transparent" data-testid="peer-biggest-gap">
          <div className="flex items-start gap-2">
            <Zap size={12} className="text-emerald-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-0.5">
                Biggest gap
              </div>
              <div className="text-sm text-ink leading-relaxed">{data.biggest_gap.message}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
